import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AppState,
  BackHandler,
  Modal,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import {
  checkForUpdate,
  openStore,
  type UpdateGate,
} from '../../services/appUpdate';
import { colors, radii, screenPadding, spacing } from '../../theme';

/**
 * The wall shown to a build that is too old to run.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A MODAL AND NOT A STAGE
 * ---------------------------------------------------------------------------
 * The same reason the call overlays are: there is no navigator, so there is no
 * stack to push onto, and a `Modal` floats over the entire tree from wherever
 * it is mounted. Mounted once at the root it therefore covers the splash, all
 * four stages, every one of `HomeScreen`'s two dozen screens, and any sheet
 * over any of them - without the stage machine below knowing it exists.
 *
 * ---------------------------------------------------------------------------
 * WHY IT RENDERS NOTHING UNTIL IT IS SURE
 * ---------------------------------------------------------------------------
 * The check is a network call, so there is a window on every launch where the
 * answer is unknown. Drawing a spinner over the app during it would put a
 * flash of blocking UI in front of every launch for every user, for a feature
 * that is off almost always. So the gate stays invisible while it asks and the
 * app runs normally underneath; if the answer comes back "blocked" it appears.
 *
 * The cost is that a blocked user gets a second or so of real app first. That
 * is the right trade: this is not a security boundary. It exists so people
 * with a broken build are told what to do, not to keep them out of anything.
 */

/**
 * Re-checked when the app returns from the background.
 *
 * Which is what makes the gate lift by itself: somebody sent to the store
 * comes back to the app after updating, and this notices without a relaunch.
 * It is also how a gate raised mid-session takes effect on a phone that has
 * been open all day.
 */
function ForceUpdateGate() {
  const [gate, setGate] = useState<UpdateGate | null>(null);

  /*
   * Aborts the in-flight check when another begins. Without it a slow reply
   * from launch can land after a fast one from a foreground event and put back
   * a gate that has just been lifted.
   */
  const inFlight = useRef<AbortController | null>(null);

  const check = useCallback(() => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;

    void checkForUpdate(controller.signal).then(result => {
      if (controller.signal.aborted) return;
      // Only ever set to something blocking, or cleared. Storing the
      // not-blocked result too would be equivalent; keeping null for it makes
      // "is the gate up" a single check everywhere below.
      setGate(result.updateRequired ? result : null);
    });
  }, []);

  useEffect(() => {
    check();

    const subscription = AppState.addEventListener('change', next => {
      if (next === 'active') check();
    });

    return () => {
      subscription.remove();
      inFlight.current?.abort();
    };
  }, [check]);

  /*
   * Android's back button must not dismiss this, and must not close the app
   * out from under somebody either - returning true swallows the press.
   * Registered only while the gate is up, so the rest of the app's back
   * handling is untouched.
   */
  useEffect(() => {
    if (!gate) return;
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => true,
    );
    return () => subscription.remove();
  }, [gate]);

  if (!gate) return null;

  return (
    <Modal
      visible
      animationType="fade"
      /* No `onRequestClose` that closes anything: there is nothing behind this
         to return to. Android requires the prop, so it re-checks instead -
         which is the one thing a press here could usefully do. */
      onRequestClose={check}
      statusBarTranslucent
    >
      <View style={styles.root}>
        <View style={styles.mark}>
          <Icon name="refresh" size={30} color={colors.primary} />
        </View>

        <Text variant="titleLg" style={styles.title}>
          Time to update
        </Text>

        <Text variant="body" color={colors.inkMuted} style={styles.body}>
          This version of Homingo is no longer supported. Update to the latest
          version to carry on booking.
        </Text>

        <Button
          label="Update now"
          size="lg"
          onPress={() => void openStore(gate.storeUrl)}
          style={styles.action}
        />

        {/* The button can fail - a device with no store app, a bad link - and
            this screen has no other way out. So the check is offered again as
            a second route rather than leaving a dead end. */}
        <Pressable onPress={check} hitSlop={8} style={styles.retry}>
          <Text variant="caption" color={colors.inkMuted}>
            Already updated? Tap to check again
          </Text>
        </Pressable>

        {/* Small, muted, and last. Useless to a customer and the first thing
            support will ask for. */}
        {gate.currentVersion || gate.minVersion ? (
          <Text variant="caption" color={colors.inkMuted} style={styles.meta}>
            {`Installed ${gate.currentVersion ?? 'unknown'}`}
            {gate.minVersion ? ` · needs ${gate.minVersion} or later` : ''}
          </Text>
        ) : null}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: screenPadding,
    gap: spacing.md,
    backgroundColor: colors.background,
  },
  mark: {
    width: 72,
    height: 72,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
  },
  title: { textAlign: 'center' },
  body: { textAlign: 'center', maxWidth: 320 },
  action: { alignSelf: 'stretch', marginTop: spacing.md },
  retry: { paddingVertical: spacing.sm },
  meta: { marginTop: spacing.lg, textAlign: 'center' },
});

export default ForceUpdateGate;
